import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { DaySequence, DaySequenceStatus } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import Button from '@/components/Button';
import {
  formatBreakTime,
  formatJobDaysSchedule,
  formatTimeRangeAndDuration,
  formatTime,
} from '@/utils/utils';
import {
  getTimesheetCardActions,
  TimesheetEditAction,
} from '@core/util/timesheetPortalActions.util';
import { useDaySequenceUtils } from './useDaySequenceUtils';

type DaySequenceItemProps = {
  daySequence: DaySequence;
  isClientPortal: boolean;
  openUpdateTimesheetConfirmation?: (daySequence: DaySequence) => void;
  openEditTimesheetModal?: (
    daySequence: DaySequence,
    editAction: TimesheetEditAction,
  ) => void;
  openCheckoutTimesheetModal?: (daySequence: DaySequence) => void;
};

const DaySequenceItem = ({
  daySequence,
  isClientPortal,
  openUpdateTimesheetConfirmation,
  openEditTimesheetModal,
  openCheckoutTimesheetModal,
}: DaySequenceItemProps) => {
  const { getStatusBadgeClass, getStatusLabel } = useDaySequenceUtils();

  const { fetchJobById, getJobById } = useJobStore();
  const job = getJobById(daySequence.jobId);

  useEffect(() => {
    if (!daySequence.jobId) return;

    const existingJob = useJobStore.getState().getJobById(daySequence.jobId);
    if (existingJob) return;

    void fetchJobById(daySequence.jobId);
  }, [daySequence.jobId, fetchJobById]);

  const cardActions = useMemo(
    () => getTimesheetCardActions(daySequence, isClientPortal),
    [daySequence, isClientPortal],
  );

  const finalisedTimesheet =
    daySequence.clockInTime &&
    daySequence.clockOutTime &&
    daySequence.status === DaySequenceStatus.ReadyForPay;

  const displayLoggedTime = useMemo(() => {
    if (daySequence.clockInTime && !daySequence.clockOutTime) {
      return `${formatTime(daySequence.clockInTime, job)} - Not set`;
    }
    return formatTimeRangeAndDuration(
      daySequence.clockInTime,
      daySequence.clockOutTime,
      job,
    );
  }, [daySequence.clockInTime, daySequence.clockOutTime, job]);

  const showProposedTimesSentMessage =
    daySequence.status === DaySequenceStatus.ProposedChange;

  const showTimesheetWithAgencyMessage =
    daySequence.status === DaySequenceStatus.EscalatedToTenant;

  // Format date as "Mon, 21 Jul"
  const formatDate = () => {
    if (daySequence.dateTime) {
      return dayjs(Number(daySequence.dateTime)).format('ddd, D MMM');
    }
    return 'Date not set';
  };

  const proposedBreaksList = daySequence.proposedBreaks ?? [];
  const hasProposedBreaks = proposedBreaksList.length > 0;

  return (
    <div className="relative rounded-lg bg-white p-6 shadow-sm">
      <span
        className={`absolute top-4 left-4 inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold ${getStatusBadgeClass(daySequence.status)}`}
      >
        {getStatusLabel(daySequence.status)}
      </span>

      <div className="mb-4 grid max-w-300 grid-cols-2 gap-y-3 border-b border-gray-200 pt-6 pb-4">
        <div>
          <span className="text-sm text-gray-600">Date</span>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-900">
            {formatDate()}
          </span>
        </div>

        <div>
          <span className="text-sm text-gray-600">Job Time</span>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-900">
            {formatJobDaysSchedule(job)}
          </span>
        </div>

        <div>
          <span className="text-sm text-gray-600">
            {finalisedTimesheet ? 'Finalised Time' : "Candidate's logged time"}
          </span>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-900">
            {displayLoggedTime}
          </span>
        </div>

        <div>
          <span className="text-sm text-gray-600">
            {finalisedTimesheet
              ? 'Finalised Break Time'
              : "Candidate's logged break time"}
          </span>
        </div>
        <div>
          <span className="text-sm font-medium text-gray-900">
            {formatBreakTime(daySequence.breaks)}
          </span>
        </div>

        {daySequence.proposedClockInTime &&
          daySequence.proposedClockOutTime && (
            <>
              <div>
                <span className="text-sm text-gray-600">
                  Client&apos;s proposed time
                </span>
              </div>
              <div>
                <span className="text-sm font-medium text-gray-900">
                  {formatTimeRangeAndDuration(
                    daySequence.proposedClockInTime,
                    daySequence.proposedClockOutTime,
                    job,
                  )}
                </span>
              </div>
            </>
          )}

        {hasProposedBreaks ? (
          <>
            <div>
              <span className="text-sm text-gray-600">
                Client&apos;s proposed break time
              </span>
            </div>
            <div>
              <span className="text-sm font-medium text-gray-900">
                {formatBreakTime(proposedBreaksList)}
              </span>
            </div>
          </>
        ) : null}
      </div>

      {cardActions.showCheckout && openCheckoutTimesheetModal ? (
        <Button
          type="button"
          variant="primary"
          onClick={() => openCheckoutTimesheetModal(daySequence)}
          className="!w-full !rounded-lg !py-2.5 !text-sm !font-medium"
        >
          Check-out
        </Button>
      ) : null}

      {(cardActions.showEdit || cardActions.showApprove) && (
        <div className="flex gap-3">
          {cardActions.showEdit &&
          cardActions.editAction &&
          openEditTimesheetModal ? (
            <Button
              type="button"
              variant="outline"
              onClick={() =>
                openEditTimesheetModal(daySequence, cardActions.editAction!)
              }
              className="!w-auto gap-2 !rounded-lg !py-2.5 !text-sm !font-medium"
            >
              <FontAwesomeIcon icon={faEdit} className="h-3 w-3" />
              Edit Timesheet
            </Button>
          ) : null}
          {cardActions.showApprove && openUpdateTimesheetConfirmation ? (
            <Button
              type="button"
              variant="primary"
              onClick={() => openUpdateTimesheetConfirmation(daySequence)}
              className="!w-auto !rounded-lg !py-2.5 !text-sm !font-medium"
            >
              Approve
            </Button>
          ) : null}
        </div>
      )}

      {showProposedTimesSentMessage ? (
        <p className="mt-4 text-center text-sm text-gray-600">
          Proposed times sent to candidate for confirmation
        </p>
      ) : null}
      {showTimesheetWithAgencyMessage ? (
        <p className="mt-4 text-center text-sm text-gray-600">
          Timesheet with agency
        </p>
      ) : null}
    </div>
  );
};

export default DaySequenceItem;
